import React, { useState, useEffect } from 'react';
import {
    Button,
    Dropdown,
    Icon,
    Image
} from 'semantic-ui-react';

import CircuitName from './CircuitName';
import AboutPill from './AboutPill';
import { proxyGravatarUrl } from './utils/avatarProxy';

const TopBarMobile = ({
    // Props from parent
    topBarColor,
    logo_path,
    home_path,
    assignment,
    creator,
    isTeacher,
    propSubmission,
    l,
    has_about,
    show_ai_chat,
    action_name,
    embed_mode,
    embed_lab_url,

    // State values
    isSaving,
    is_labLoaded,
    is_public,
    setPublic,
    setShareOpen,
    user,
    userProjects,
    subscription_name,
    title,
    submittedAt,
    submission,
    mark,
    setMark,
    markCaption,
    setMarkCaption,
    zoomValue,
    visible,
    setVisible,
    aiVisible,
    setAiVisible,
    userMenu,
    circuit,

    // Mobile slides overlay
    mobileSlidesOpen,
    setMobileSlidesOpen,

    // Functions
    onSave,
    save,
    showSubmitedAnswerDialog,
    saveMark,
    userMenuChange,
    onZoomMenuChange,

    // Computed values
    show_save_buttons
}) => {
    const [menuOpen, setMenuOpen] = useState(false);

    // Close menu when clicking anywhere (including canvas)
    useEffect(() => {
        if (!menuOpen) return;

        const handleGlobalClick = (e) => {
            // Check if click is on the dropdown or menu
            const dropdownElement = document.querySelector('#top-bar-mobile');
            if (dropdownElement && dropdownElement.contains(e.target)) {
                return; // Don't close if clicking inside top bar
            }
            setMenuOpen(false);
        };

        // Use capture phase to catch events before canvas stops propagation
        window.addEventListener('mousedown', handleGlobalClick, true);
        window.addEventListener('touchstart', handleGlobalClick, true);

        return () => {
            window.removeEventListener('mousedown', handleGlobalClick, true);
            window.removeEventListener('touchstart', handleGlobalClick, true);
        };
    }, [menuOpen]);

    // Build menu items based on available actions
    const buildMenuItems = () => {
        const items = [];

        // User profile at top of menu (when logged in) - clickable to open profile in new tab
        if (user) {
            items.push({
                key: 'user-profile',
                content: (
                    <a
                        href={`/users/${user.id}/profile`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '4px 0',
                            color: 'inherit',
                            textDecoration: 'none'
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setMenuOpen(false);
                        }}
                    >
                        {user.avatar ? (
                            <img
                                src={proxyGravatarUrl(user.avatar)}
                                alt={user.name || 'User'}
                                crossOrigin="anonymous"
                                referrerPolicy="no-referrer"
                                style={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: '50%',
                                    marginRight: 10,
                                    objectFit: 'cover',
                                    backgroundColor: 'var(--muted)'
                                }}
                            />
                        ) : (
                            <div style={{
                                width: 28,
                                height: 28,
                                borderRadius: '50%',
                                marginRight: 10,
                                backgroundColor: 'var(--primary)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'var(--primary-foreground)',
                                fontSize: 14,
                                fontWeight: 'bold'
                            }}>
                                {(user.name || user.email || 'U').charAt(0).toUpperCase()}
                            </div>
                        )}
                        <div style={{ fontSize: 13 }}>
                            <span style={{ color: 'var(--muted-foreground)' }}>{l.signed_in_as || 'Signed in as'} </span>
                            <span style={{ fontWeight: 'bold', color: 'var(--foreground)' }}>{user.name || user.email || 'User'}</span>
                        </div>
                    </a>
                ),
                style: { opacity: 1, cursor: 'pointer' }
            });
            items.push({ key: 'profile-divider', divider: true });
        }

        // Sign In / Sign Up (when not logged in)
        if (!user) {
            items.push({
                key: 'sign-in',
                icon: 'sign in',
                text: l.sign_in || 'Sign In',
                onClick: () => window.open('/users/sign_in/', '_blank')
            });
            items.push({
                key: 'sign-up',
                icon: 'user plus',
                text: l.create_an_account || 'Sign Up',
                onClick: () => window.open('/users/sign_up', '_blank')
            });
            items.push({ key: 'auth-divider', divider: true });
        }

        // New circuit
        {
            items.push({
                key: 'new',
                icon: 'file outline',
                text: l.new,
                onClick: () => window.open("/lab", '_blank')
            });
        }

        // Save
        if (show_save_buttons) {
            items.push({
                key: 'save',
                icon: 'save',
                text: l.save,
                onClick: () => onSave()
            });
        }



        // Share
        if (is_labLoaded && !isSaving && circuit && circuit.is_experiment_created) {
            items.push({
                key: 'share',
                icon: 'share',
                text: l.share,
                onClick: () => setShareOpen(true)
            });
        }

        // Public/Private toggle
        if (show_save_buttons) {
            items.push({
                key: 'public',
                icon: is_public ? 'eye' : 'eye slash',
                text: is_public ? l.public : (l.private || 'Private'),
                onClick: () => {
                    if (subscription_name == 'starter') {
                        window.thumbs.alert("Private circuit is not available in starter version");
                    } else {
                        setPublic(!is_public);
                    }
                }
            });
        }

        // My Experiments        
        {
            items.push({
                key: 'experiments',
                icon: 'flask',
                text: l.experiments,
                onClick: () => window.location.href = '/users_experiments'
            });
        }

        // Assignment save (for students)
        if (is_labLoaded && action_name == 'assignment' && user != null && !isTeacher &&
            (submittedAt === null || propSubmission?.assignment?.settings?.resubmit_allowed) &&
            creator && submission && submission.user_id == user.id && !isSaving) {

            items.push({
                key: 'assignment-save',
                icon: 'save',
                text: l.save,
                onClick: () => {
                    window.thumbs.refresh_current_thumb();
                    setTimeout(() => save(assignment.id), 1);
                }
            });

            if (title != undefined) {
                items.push({
                    key: 'submit',
                    icon: 'send',
                    text: propSubmission?.submitted_at == null ? l.submit_answer :
                        (propSubmission?.assignment?.settings?.resubmit_allowed ? l.resubmit_answer : l.submit_answer),
                    onClick: () => showSubmitedAnswerDialog(true)
                });
            }
        }

        // Divider before view options
        if (items.length > 0) {
            items.push({ key: 'divider-1', divider: true });
        }

        // AI Chat toggle
        if (show_ai_chat) {
            items.push({
                key: 'ai-chat',
                icon: 'comment',
                text: l.ai_chat || 'AI Chat',
                onClick: () => setAiVisible(!aiVisible)
            });
        }

        // Zoom options
        items.push({ key: 'divider-2', divider: true });

        // Slides overlay toggle (mobile only)
        items.push({
            key: 'slides',
            icon: 'grid layout',
            text: l.slides || 'Slides',
            onClick: () => {
                setMenuOpen(false);
                setMobileSlidesOpen(true);
            }
        });

        // Show All option
        items.push({
            key: 'zoom-show-all',
            icon: 'expand',
            text: l.show_all || 'Show All',
            onClick: () => {
                if (window.com && window.com.clientside && window.com.clientside.main) {
                    window.com.clientside.main.Html5Bootstrap.jsCall('show-all', null, null);
                }
            }
        });

        const zoomLevels = [
            { value: 0.5, text: '50%' },
            { value: 0.75, text: '75%' },
            { value: 1, text: '100%' },
            { value: 1.25, text: '125%' },
            { value: 1.5, text: '150%' }
        ];

        zoomLevels.forEach(zoom => {
            const isSelected = Math.abs(zoomValue - zoom.value) < 0.01;
            items.push({
                key: `zoom-${zoom.value}`,
                icon: isSelected ? 'check' : 'zoom',
                text: zoom.text,
                onClick: () => onZoomMenuChange(null, { value: zoom.value })
            });
        });

        // User menu items
        if (user && userMenu && userMenu.length > 0) {
            items.push({ key: 'divider-3', divider: true });

            userMenu.forEach((menuItem, index) => {
                // Skip disabled items and "Signed in as" (already shown at top with profile link)
                if (menuItem.disabled || menuItem.is_profile_header) {
                    return;
                }

                if (menuItem.divider) {
                    items.push({ key: `user-divider-${index}`, divider: true });
                } else {
                    // Pick appropriate icon based on menu item
                    let icon = menuItem.icon;
                    if (!icon) {
                        const text = (menuItem.text || menuItem.name || '').toLowerCase();
                        const value = (menuItem.value || '').toLowerCase();

                        // "Signed in as" item should show user icon (profile link)
                        if (text.includes('signed in') || text.includes('profile') || value.includes('profile')) {
                            icon = 'user';
                        } else if (text.includes('setting') || value.includes('setting')) {
                            icon = 'setting';
                        } else if (text.includes('logout') || value === 'logout' || text.includes('sign out')) {
                            icon = 'sign out';
                        } else if (text.includes('dashboard') || value.includes('dashboard')) {
                            icon = 'dashboard';
                        } else if (text.includes('experiment') || value.includes('experiment')) {
                            icon = 'flask';
                        } else if (text.includes('class') || value.includes('class')) {
                            icon = 'users';
                        } else if (text.includes('help') || value.includes('help')) {
                            icon = 'help circle';
                        } else if (text.includes('subscription') || value.includes('subscription')) {
                            icon = 'credit card';
                        } else {
                            icon = 'linkify';
                        }
                    }

                    items.push({
                        key: `user-${index}`,
                        icon: icon,
                        text: menuItem.text || menuItem.name,
                        onClick: (e) => {
                            // Close menu first
                            setMenuOpen(false);
                            // Small delay to ensure menu closes before navigation
                            setTimeout(() => {
                                // Use userMenuChange with value (URL or "logout")
                                // Profile links will open in new tab (handled in LabLayout.js userMenuChange)
                                userMenuChange(null, { value: menuItem.value });
                            }, 100);
                        }
                    });
                }
            });
        }

        return items;
    };

    const menuItems = buildMenuItems();

    // Determine if we should show experiment name or logo
    const hasExperimentName = title || (circuit && circuit.name) || (assignment && assignment.name);

    // Show creator when viewing someone else's experiment
    const showCreator = is_labLoaded && creator && creator.name != null && (user == null || creator.id != user.id);

    return (
        <div id="top-bar-mobile" style={{
            backgroundColor: topBarColor || 'var(--sidebar)',
            height: 50,
            padding: '0 10px',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
        }}>
            {/* Left: Logo OR Creator + Experiment Name */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                flex: 1,
                overflow: 'hidden',
                minWidth: 0
            }}>
                {hasExperimentName ? (
                    /* Show creator image + experiment name when available */
                    <>
                        {showCreator && (
                            <a
                                href={creator.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    marginRight: 2,
                                    flexShrink: 0,
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                            >
                                <img
                                    src={proxyGravatarUrl(creator.avatar)}
                                    alt={creator.name}
                                    crossOrigin="anonymous"
                                    referrerPolicy="no-referrer"
                                    style={{
                                        width: 28,
                                        height: 28,
                                        borderRadius: '50%',
                                        objectFit: 'cover',
                                        backgroundColor: 'var(--muted)'
                                    }}
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                    }}
                                />
                            </a>
                        )}
                        {has_about && <AboutPill compact label={l.about_circuit} />}
                        <div style={{
                            flex: 1,
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                            color: 'var(--sidebar-foreground)',
                            fontSize: 14,
                            fontWeight: 500,
                            marginLeft: showCreator ? -13 : 0
                        }}>
                            <CircuitName
                                isSaving={isSaving}
                                assignment={assignment}
                                circuit={circuit}
                                title={title}
                                is_public={is_public}
                                compact={true}
                            />
                        </div>
                    </>
                ) : (
                    /* Show logo when no experiment name */
                    <a href={home_path} style={{ display: 'flex', alignItems: 'center' }}>
                        <Image
                            src={logo_path}
                            style={{
                                height: 30,
                                width: 'auto',
                                maxWidth: 120,
                                objectFit: 'contain',
                                filter: logo_path.indexOf("CircoalreS") != -1 ? 'invert()' : 'none'
                            }}
                        />
                    </a>
                )}
            </div>

            {embed_mode && embed_lab_url &&
                <a href={embed_lab_url} target="_top" style={{ marginLeft: 8, flexShrink: 0, fontSize: 13, fontWeight: 700, color: 'var(--primary)', whiteSpace: 'nowrap' }}>{l.open_in_lab || 'Open in the lab'}</a>
            }

            {/* Right: Menu Dropdown */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                marginLeft: 10
            }}>
                <Dropdown
                    trigger={
                        <Button
                            icon
                            size='small'
                            style={{
                                background: 'var(--accent)',
                                color: 'var(--accent-foreground)',
                                margin: 0
                            }}
                        >
                            <Icon name='bars' />
                        </Button>
                    }
                    icon={null}
                    direction='left'
                    pointing='top right'
                    open={menuOpen}
                    onOpen={() => setMenuOpen(true)}
                    onClose={() => setMenuOpen(false)}
                >
                    <Dropdown.Menu style={{
                        maxHeight: '70vh',
                        overflowY: 'auto',
                        minWidth: 200
                    }}>
                        {menuItems.map(item => {
                            if (item.divider) {
                                return <Dropdown.Divider key={item.key} />;
                            }
                            if (item.content) {
                                return (
                                    <Dropdown.Item
                                        key={item.key}
                                        disabled={item.disabled}
                                        style={item.style}
                                    >
                                        {item.content}
                                    </Dropdown.Item>
                                );
                            }
                            return (
                                <Dropdown.Item
                                    key={item.key}
                                    icon={item.icon}
                                    text={item.text}
                                    onClick={item.onClick}
                                    selected={false}
                                    style={{ backgroundColor: 'transparent' }}
                                />
                            );
                        })}
                    </Dropdown.Menu>
                </Dropdown>
            </div>
        </div>
    );
};

export default TopBarMobile;
